
import React, { useMemo, useState } from 'react';
import type { TimeOffRequest, TimeOffRequestStatus, AssignmentStatus, StaffMember, TimeOffRequestType } from '../../types';
import { Icons } from '../ui/Icons';
import Popover from '../ui/Popover';
import { useDeleteHandler } from '../../hooks/useDeleteHandler';
import ConfirmationModal from '../ui/ConfirmationModal';

interface TimeOffManagementViewProps {
    requests: TimeOffRequest[];
    staffList: StaffMember[];
    onUpdateRequestStatus: (requestId: string, status: 'APPROVED' | 'REJECTED', scheduleStatus?: AssignmentStatus) => void;
    onDeleteRequest: (requestId: string) => void;
    onCreateRequest: (request: Omit<TimeOffRequest, 'id' | 'status' | 'createdAt' | 'staffName'>) => void;
}

const statusConfig: { [key in TimeOffRequestStatus]: { text: string; bg: string; icon: React.ReactNode; label: string; } } = {
    PENDING_REPLACER: { text: 'text-orange-800 dark:text-orange-300', bg: 'bg-orange-100 dark:bg-orange-900/25', icon: <Icons.Users className="w-4 h-4" />, label: 'Pending Cover' },
    PENDING_ADMIN: { text: 'text-yellow-800 dark:text-yellow-300', bg: 'bg-yellow-100 dark:bg-yellow-900/25', icon: <Icons.Clock className="w-4 h-4" />, label: 'Pending Admin' },
    APPROVED: { text: 'text-green-800 dark:text-green-300', bg: 'bg-green-100 dark:bg-green-900/25', icon: <Icons.CalendarCheck className="w-4 h-4" />, label: 'Approved' },
    REJECTED: { text: 'text-red-800 dark:text-red-300', bg: 'bg-red-100 dark:bg-red-900/25', icon: <Icons.X className="w-4 h-4" />, label: 'Rejected' },
};

const TimeOffManagementView: React.FC<TimeOffManagementViewProps> = ({ requests, staffList, onUpdateRequestStatus, onDeleteRequest, onCreateRequest }) => {
    
    const staffMap = useMemo(() => new Map(staffList.map(s => [s.id, s.name])), [staffList]);

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [newRequest, setNewRequest] = useState<{
        employeeId: string;
        startDate: string;
        endDate: string;
        reason: string;
        type: TimeOffRequestType;
        replacementEmployeeId: string;
    }>({
        employeeId: '',
        startDate: '',
        endDate: '',
        reason: '',
        type: 'DIRECT',
        replacementEmployeeId: ''
    });

    const {
        itemToDelete,
        isDeleting,
        initiateDelete,
        confirmDelete,
        cancelDelete
    } = useDeleteHandler<TimeOffRequest>({
        onDeleteAsync: async (req) => onDeleteRequest(req.id),
        entityName: 'Time Off Request',
        getDisplayName: (req) => `for ${req.staffName}`,
        getItemId: (req) => req.id,
    });


    const sortedRequests = [...requests].sort((a, b) => {
        // Pending requests first, then by date
        if (a.status.startsWith('PENDING') && !b.status.startsWith('PENDING')) return -1;
        if (!a.status.startsWith('PENDING') && b.status.startsWith('PENDING')) return 1;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    const approvalOptions: { label: string, status: AssignmentStatus, icon: React.ReactNode }[] = [
        { label: 'As Vacation', status: 'VACATION', icon: <Icons.Plane className="w-4 h-4 text-blue-500" /> },
        { label: 'As Sick Day', status: 'SICK', icon: <Icons.Thermometer className="w-4 h-4 text-yellow-500" /> },
        { label: 'As Off Day', status: 'OFF', icon: <Icons.Coffee className="w-4 h-4 text-gray-500" /> },
    ];
    
    const ApprovalPopoverContent = ({ close, requestId }: { close: () => void; requestId: string }) => (
        <div className="w-48">
            <div className="p-2 text-sm font-semibold text-gray-600 dark:text-gray-300 border-b dark:border-slate-700">Approve request as:</div>
             <div className="p-1">
                {approvalOptions.map(({ label, status, icon }) => (
                    <button
                        key={status}
                        onClick={() => {
                            onUpdateRequestStatus(requestId, 'APPROVED', status);
                            close();
                        }}
                        className="w-full flex items-center gap-3 text-left px-3 py-2 text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md"
                    >
                        {icon}
                        <span>{label}</span>
                    </button>
                ))}
            </div>
        </div>
    );

    const handleCreateSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onCreateRequest({
            employeeId: newRequest.employeeId,
            startDate: newRequest.startDate,
            endDate: newRequest.endDate,
            reason: newRequest.reason,
            type: newRequest.type,
            replacementEmployeeId: newRequest.type === 'REPLACED' ? newRequest.replacementEmployeeId : null
        });
        setIsCreateModalOpen(false);
        setNewRequest({
            employeeId: '',
            startDate: '',
            endDate: '',
            reason: '',
            type: 'DIRECT',
            replacementEmployeeId: ''
        });
    };

    return (
        <div className="max-w-6xl mx-auto">
            <div className="mb-6 flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Time Off Requests</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Review and approve or reject time off requests from staff.</p>
                </div>
                <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors shadow-sm text-sm font-medium"
                >
                    <Icons.Plus className="w-4 h-4" />
                    Create Request
                </button>
            </div>
             <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                        <thead className="bg-gray-50 dark:bg-slate-900/50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Staff Member</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Dates</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Replacement</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                             {sortedRequests.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="text-center py-10 text-gray-500 dark:text-gray-400">
                                        There are no time off requests.
                                    </td>
                                </tr>
                            )}
                            {sortedRequests.map((req) => (
                                <tr key={req.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{req.staffName}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">{req.reason || 'No reason provided'}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{req.startDate} to {req.endDate}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${req.type === 'DIRECT' ? 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-200' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/25 dark:text-blue-300'}`}>
                                            {req.type}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                        {req.type === 'REPLACED' && req.replacementEmployeeId ? staffMap.get(req.replacementEmployeeId) || 'Unknown Staff' : 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig[req.status].bg} ${statusConfig[req.status].text}`}>
                                            {statusConfig[req.status].icon}
                                            {statusConfig[req.status].label}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                        <div className="flex justify-center items-center gap-2">
                                            {req.status === 'PENDING_ADMIN' && (
                                                <>
                                                    <Popover
                                                        content={<ApprovalPopoverContent requestId={req.id} close={() => {}} />}
                                                    >
                                                        <button className="px-3 py-1 rounded-md text-xs bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/25 dark:text-green-300 dark:hover:bg-green-900/40 flex items-center gap-1">
                                                            Approve
                                                            <Icons.ChevronDown className="w-3 h-3" />
                                                        </button>
                                                    </Popover>
                                                    <button onClick={() => onUpdateRequestStatus(req.id, 'REJECTED')} className="px-3 py-1 rounded-md text-xs bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900/25 dark:text-red-300 dark:hover:bg-red-900/40">Reject</button>
                                                </>
                                            )}
                                            {req.status === 'PENDING_REPLACER' && (
                                                <span className="text-xs italic text-gray-500 dark:text-gray-400">Awaiting cover</span>
                                            )}
                                            <button 
                                                onClick={() => initiateDelete(req)}
                                                className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                                                title="Delete Request"
                                            >
                                                <Icons.Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {isCreateModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Create Time Off Request</h3>
                            <button onClick={() => setIsCreateModalOpen(false)} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
                                <Icons.X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleCreateSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Employee</label>
                                <select
                                    required
                                    value={newRequest.employeeId}
                                    onChange={(e) => setNewRequest({ ...newRequest, employeeId: e.target.value })}
                                    className="w-full border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                >
                                    <option value="">Select an employee</option>
                                    {staffList.filter(s => s.status !== 'deleted').map(staff => (
                                        <option key={staff.id} value={staff.id}>{staff.name}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                                    <input
                                        type="date"
                                        required
                                        value={newRequest.startDate}
                                        onChange={(e) => setNewRequest({ ...newRequest, startDate: e.target.value })}
                                        className="w-full border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                                    <input
                                        type="date"
                                        required
                                        value={newRequest.endDate}
                                        onChange={(e) => setNewRequest({ ...newRequest, endDate: e.target.value })}
                                        className="w-full border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                                <div className="flex gap-4">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="type"
                                            value="DIRECT"
                                            checked={newRequest.type === 'DIRECT'}
                                            onChange={() => setNewRequest({ ...newRequest, type: 'DIRECT', replacementEmployeeId: '' })}
                                            className="text-indigo-600 focus:ring-indigo-500 dark:bg-slate-700 dark:border-slate-600"
                                        />
                                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Direct Request</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="type"
                                            value="REPLACED"
                                            checked={newRequest.type === 'REPLACED'}
                                            onChange={() => setNewRequest({ ...newRequest, type: 'REPLACED' })}
                                            className="text-indigo-600 focus:ring-indigo-500 dark:bg-slate-700 dark:border-slate-600"
                                        />
                                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Find Cover</span>
                                    </label>
                                </div>
                            </div>

                            {newRequest.type === 'REPLACED' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Replacement Employee</label>
                                    <select
                                        required
                                        value={newRequest.replacementEmployeeId}
                                        onChange={(e) => setNewRequest({ ...newRequest, replacementEmployeeId: e.target.value })}
                                        className="w-full border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                    >
                                        <option value="">Select a replacement</option>
                                        {staffList
                                            .filter(s => s.status !== 'deleted' && s.id !== newRequest.employeeId)
                                            .map(staff => (
                                                <option key={staff.id} value={staff.id}>{staff.name}</option>
                                            ))}
                                    </select>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason (Optional)</label>
                                <textarea
                                    value={newRequest.reason}
                                    onChange={(e) => setNewRequest({ ...newRequest, reason: e.target.value })}
                                    rows={2}
                                    className="w-full border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                />
                            </div>

                            <div className="pt-4 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsCreateModalOpen(false)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md hover:bg-gray-50 dark:hover:bg-slate-600"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 shadow-sm"
                                >
                                    Create Request
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

             {itemToDelete && (
                <ConfirmationModal
                    title="Delete Time Off Request"
                    message={`Are you sure you want to permanently delete the time off request for ${itemToDelete.staffName} from ${itemToDelete.startDate} to ${itemToDelete.endDate}?`}
                    confirmButtonText="Delete"
                    onConfirm={confirmDelete}
                    onCancel={cancelDelete}
                    isConfirming={isDeleting}
                />
            )}
        </div>
    );
};

export default TimeOffManagementView;
